/*
  Intercepts product form submits, adds to the cart with the Ajax API and shows
  a bottom-right notification instead of navigating to /cart.

  Forms opt in with `data-ajax-cart`. Anything else (dynamic checkout buttons,
  forms without the attribute) keeps its native behaviour.
*/
(() => {
  class CartNotification extends HTMLElement {
    constructor() {
      super();
      this.onSubmit = this.onSubmit.bind(this);
      this.hide = this.hide.bind(this);
      this.timer = null;
    }

    connectedCallback() {
      document.addEventListener('submit', this.onSubmit);
      this.addEventListener('mouseenter', () => clearTimeout(this.timer));
      this.addEventListener('mouseleave', () => this.scheduleHide());
       this.refreshCount();
    }

    disconnectedCallback() {
      document.removeEventListener('submit', this.onSubmit);
    }

    async onSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-ajax-cart')) return;

      event.preventDefault();

      const body = new FormData(form);

      // Stock gate: block the add (and leave the cart count untouched) when it
      // would exceed available inventory, showing the shared limit popup.
      const limitApi = window.CartLimit;
      if (limitApi) {
        const variantId = body.get('id');
        const requested = Number(body.get('quantity')) || 1;
        const limit = limitApi.formLimit(form);
        try {
          const cartRes = await fetch(`${window.Shopify.routes.root}cart.js`, {
            headers: { Accept: 'application/json' },
          });
          const cart = await cartRes.json();
          const inCart = limitApi.cartQtyForVariant(cart, variantId);
          if (inCart + requested > limit) {
            // Reuse the same bottom-right toast as a normal add, in its error
            // state, instead of a separate modal. The cart count is left alone.
            const message = limitApi.buildLimitMessage(limit, inCart);
            this.renderError(message.text);
            this.show();
            return;
          }
        } catch (err) {
          // If the check fails, fall through and let Shopify enforce stock.
        }
      }

      const button = event.submitter || form.querySelector('[type="submit"]');
      if (button) button.setAttribute('aria-busy', 'true');

      body.append('sections_url', window.location.pathname);

      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body,
        });
        const item = await response.json();

        if (!response.ok) {
          this.renderError(item.description || item.message);
        } else {
          this.renderItem(item);
          this.refreshCount();
        }
      } catch (error) {
        this.renderError(this.dataset.errorText);
      } finally {
        if (button) button.removeAttribute('aria-busy');
        this.show();
      }
    }

    renderItem(item) {
      this.dataset.state = 'added';
      this.setText(
        '[data-cart-notification-text]',
        (this.dataset.addedText || '{{ title }} added to cart').replace('{{ title }}', item.product_title)
      );
    }

    renderError(message) {
      this.dataset.state = 'error';
      this.setText('[data-cart-notification-text]', message || this.dataset.errorText);
    }

    async refreshCount() {
      try {
        const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
          headers: { Accept: 'application/json' },
        });
        const cart = await response.json();

        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      } catch (error) {
        /* The item is in the cart either way; the badge catches up on reload. */
      }
    }

    setText(selector, value) {
      const el = this.querySelector(selector);
      if (el) el.textContent = value ?? '';
    }

    show() {
      this.setAttribute('open', '');
      this.scheduleHide();
    }

    scheduleHide() {
      clearTimeout(this.timer);
      const delay = Number(this.dataset.duration || 6000);
      if (delay > 0) this.timer = setTimeout(this.hide, delay);
    }

    hide() {
      clearTimeout(this.timer);
      this.removeAttribute('open');
    }
  }

  customElements.define('cart-notification', CartNotification);
})();
